import { useCallback, useEffect, useState } from 'react';
import { reportError } from 'utils/reporting';
import { showNotification } from 'common/function';
import { isApiError } from 'common/types';
import { useCurrentUser } from './useCurrentUser';
import { FeatureFlag, TenancyService, TenantInfo } from 'types';
import { useFeatureFlag } from 'hooks/featureFlags/useFeatureFlag';
import { LegacyTenancyService } from 'service/TenancyService';

export function useTenants() {
  const { user } = useCurrentUser();
  const [availableTenants, setAvailableTenants] = useState<TenantInfo[]>([]);
  const [currentTenant, setCurrentTenant] = useState<TenantInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const isTenantManagementEnabled = useFeatureFlag(FeatureFlag.TENANCY_MANAGEMENT);

  const loadAvailableTenants = useCallback(async () => {
    if (!user) {
      return;
    }

    setIsLoading(true);

    try {
      const tenants = isTenantManagementEnabled ? await TenancyService.listTenancies() : await LegacyTenancyService.listTenancies(user);
      setAvailableTenants(tenants);

      const currentTenant = tenants.find(tenant => tenant.is_current) || null;
      setCurrentTenant(currentTenant);
    } catch (err: any) {
      reportError(err);
      showNotification(isApiError(err) ? err.body.error : err.message, 'Load Tenants Error', 'error');
    }
    setIsLoading(false);
  }, [user, isTenantManagementEnabled]);

  const isMultiTenant = availableTenants.length > 1;

  useEffect(() => {
    loadAvailableTenants();
  }, [loadAvailableTenants]);

  return {
    availableTenants,
    currentTenant,
    isLoading,
    isMultiTenant,
    loadAvailableTenants,
  };
}
